import { AIMessageContext } from '@diagon-alley/common';
import useAIChatRenderContext from '@hooks/ai/useAIChatRenderContext';
import { ActionIcon, Box, Group, Text, Tooltip } from '@mantine/core';
import { PlusIcon } from '@phosphor-icons/react';
import useAiAnalystContext from '@providers/AiAnalyst/useAiAnalystContext';
import { motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ChatTitleInput from './ChatTitleInput';

const ChatHeader = () => {
    const {
        aiChatMessages,
        activeThread,
        isLoadingActiveThreadMessages,
        isStreaming,
    } = useAiAnalystContext((context) => context.state);
    const { setShowAISidebar, startNewThread, updateThreadById } =
        useAiAnalystContext((context) => context.actions);
    const { getCurrentContext: currentChatContext } = useAIChatRenderContext();

    const [isNewThreadLoading, setIsNewThreadLoading] = useState(false);
    const [currentThreadName, setCurrentThreadName] = useState('');
    const [isInputExpanded, setIsInputExpanded] = useState(false);

    useEffect(() => {
        if (activeThread) {
            setCurrentThreadName(activeThread.threadName);
        }
    }, [activeThread]);

    const isRenderedInHomePage = useMemo(() => {
        return currentChatContext === AIMessageContext.HOME_PAGE;
    }, [currentChatContext]);

    const handleStartNewThread = useCallback(async () => {
        setIsNewThreadLoading(true);
        await startNewThread();
        setIsNewThreadLoading(false);
    }, [startNewThread]);

    const hasMessages = aiChatMessages.length > 0;

    const saveThreadName = useCallback(() => {
        if (activeThread) {
            if (currentThreadName === activeThread.threadName) {
                return;
            }
            void updateThreadById(activeThread.threadId, {
                threadName: currentThreadName,
            });
        }
    }, [activeThread, currentThreadName, updateThreadById]);

    const handleInputClick = useCallback(() => {
        if (hasMessages && !isInputExpanded) {
            setIsInputExpanded(true);
        }
    }, [hasMessages, isInputExpanded]);

    const handleInputBlur = useCallback(() => {
        setIsInputExpanded(false);
        saveThreadName();
    }, [saveThreadName]);

    const handleResetInput = useCallback(() => {
        if (!activeThread) return;
        setIsInputExpanded(false);
        setCurrentThreadName(activeThread.threadName);
    }, [activeThread]);

    const renderNewThreadButton = useMemo(() => {
        return (
            <Tooltip label="New chat" offset={4} position="top" withinPortal>
                <ActionIcon
                    variant="light"
                    color="gray"
                    radius="xl"
                    size="md"
                    className="shadow-sm hover:shadow"
                    onClick={handleStartNewThread}
                    disabled={isNewThreadLoading}
                >
                    <PlusIcon size={16} />
                </ActionIcon>
            </Tooltip>
        );
    }, [handleStartNewThread, isNewThreadLoading]);

    const renderInput = useMemo(() => {
        return (
            <ChatTitleInput
                value={currentThreadName}
                onChange={setCurrentThreadName}
                onBlur={handleInputBlur}
                onClick={handleInputClick}
                isInputExpanded={isInputExpanded}
                isLoading={isLoadingActiveThreadMessages || isStreaming}
                handleResetInput={handleResetInput}
            />
        );
    }, [
        currentThreadName,
        handleInputBlur,
        handleInputClick,
        isInputExpanded,
        isLoadingActiveThreadMessages,
        isStreaming,
        handleResetInput,
    ]);

    if (isRenderedInHomePage) {
        return (
            <motion.div
                className="sticky top-0 flex w-full justify-center p-3"
                animate={{
                    borderBottomWidth: hasMessages ? 1 : 0,
                    borderBottomColor: hasMessages
                        ? 'rgba(255, 255, 255, 0.1)'
                        : 'rgba(255, 255, 255, 0)',
                }}
                transition={{
                    duration: 0.3,
                    delay: hasMessages ? 0.3 : 0, // Delay border appearance until width transition completes
                }}
                style={{
                    borderBottomStyle: 'solid',
                }}
            >
                <Group className="flex w-full max-w-5xl items-center gap-3">
                    <Box className="flex-1">{renderInput}</Box>
                    <Group className="items-center gap-2">
                        {renderNewThreadButton}
                    </Group>
                </Group>
            </motion.div>
        );
    }

    return (
        <Group className="flex w-full items-center justify-between gap-1 border-b border-white/10 py-3 pr-2 pl-2">
            <Box className="flex-1 flex-nowrap truncate overflow-hidden">
                {hasMessages ? (
                    <>{renderInput}</>
                ) : (
                    <Box className="flex items-center gap-1 pl-2">
                        <Text className="text-base font-medium text-gray-800">
                            AI Assistant
                        </Text>
                    </Box>
                )}
            </Box>

            <Group className="items-center gap-0">
                {hasMessages && (
                    <Box onClick={handleStartNewThread}>
                        {renderNewThreadButton}
                    </Box>
                )}

                <button
                    type="button"
                    className="flex cursor-pointer items-center justify-center rounded-md px-2 py-1 text-sm font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowAISidebar(false);
                    }}
                >
                    Close
                </button>
            </Group>
        </Group>
    );
};

export default React.memo(ChatHeader);
